import { getRailsClient, getRailsFormClient } from "../client";

// Retrieves notes for a specific lesson id
export const getNotes = async ({ lessonId, levelId }: { lessonId: string; levelId?: string }) => {
  const client = getRailsClient();
  const Route = `/api/internal_api/v1/levels/${levelId}/lessons/${lessonId}/notes`;

  const { data } = await client.get(Route);
  const formattedNotes = data.userNotes.map(transformNoteData);
  // Sort the notes by timestamp, most recent first
  return formattedNotes.sort(
    (a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

// Creates a note
export const createNote = async ({
  lessonId,
  noteText,
  levelId,
}: {
  lessonId: string;
  noteText: string;
  levelId?: string;
}) => {
  const Route = `/api/internal_api/v1/levels/${levelId}/lessons/${lessonId}/notes`;

  const formData = new FormData();
  formData.append("user_note[text]", noteText);

  const client = getRailsFormClient();
  const { data } = await client.post(Route, formData);
  return transformNoteData(data.userNote);
};

// Update a note
export const updateNote = async ({
  lessonId,
  noteId,
  noteText,
  levelId,
}: {
  lessonId?: string;
  noteId: string;
  noteText: string;
  levelId?: string;
}) => {
  // Use profile route if no lessonId provided, otherwise use lesson-specific route
  const Route = lessonId
    ? `/api/internal_api/v1/levels/${levelId}/lessons/${lessonId}/notes/${noteId}`
    : `/api/internal_api/v1/current_user/notes/${noteId}`;

  const formData = new FormData();
  // Lesson specific route uses "user_note[text]", profile route uses "note[text]"
  const fieldName = lessonId ? "user_note[text]" : "note[text]";
  formData.append(fieldName, noteText);

  const client = getRailsFormClient();
  const { data } = await client.put(Route, formData);

  // Lesson notes return userNote, profile notes return raw data
  if (!data.userNote) {
    return data;
  }

  return transformNoteData(data.userNote);
};

// Deletes a note for a specific lesson id
export const deleteNote = async ({
  lessonId,
  noteId,
  levelId,
}: {
  lessonId: string;
  noteId: string;
  levelId?: string;
}) => {
  const Route = `/api/internal_api/v1/levels/${levelId}/lessons/${lessonId}/notes/${noteId}`;
  const client = getRailsClient();
  await client.delete(Route);
};

// Transforms note data from the API to the format used in the frontend
function transformNoteData(noteData: any) {
  return {
    id: noteData.id,
    text: noteData.text,
    timestamp: new Date(noteData.createdAt).toLocaleString(),
  };
}

export const getAllUserNotes = async () => {
  const API = `/api/internal_api/v1/current_user/notes`;

  try {
    const { data } = await getRailsClient().get(API);
    return data;
  } catch (error) {
    console.error("Error fetching all user notes:", error);
    throw error;
  }
};

export const downloadAllUserNotes = async (format: "csv" | "txt") => {
  const API = `/api/internal_api/v1/current_user/notes.${format}`;

  try {
    const response = await getRailsClient().get(API);

    // Create a link to download the file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `user_notes.${format}`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  } catch (error) {
    console.error(`Error downloading user notes as ${format}:`, error);
    throw error;
  }
};
